import { MIN_PASSWORD_LENGTH } from "@vidimo/shared/contracts";
import { useCallback, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTranslations } from "use-intl";
import { authErrorMessage } from "@/auth/auth-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import {
  AuthError,
  AuthScreen,
  FormHeading,
  useEnterFocuses,
} from "./auth-screen";
import { FilingExceptionNotice } from "./filing-exception-notice";
import { LegalConsent } from "./legal-consent";
import { signupLanguageData, trialLinkQuery } from "./signup-language";
import { SocialSignIn } from "./social-sign-in";

export default function SignUpClient() {
  const t = useTranslations("auth.signUp");
  const history = useHistory();
  const { search } = useLocation();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t("errorNameRequired"));
      return;
    }
    if (!(email && password)) {
      setError(t("errorCredentialsRequired"));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("errorPasswordTooShort"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error: signUpError } = await authClient.signUp.email({
        email,
        name: name.trim(),
        password,
        ...signupLanguageData(search),
      });

      if (signUpError) {
        setError(authErrorMessage(signUpError));
      } else {
        // Sign-up auto-signs-in (verification is not required), so send them
        // straight into the app.
        history.replace("/");
      }
    } catch (err: unknown) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value),
    []
  );
  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
    []
  );
  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
    []
  );
  const handleNameEnter = useEnterFocuses(emailRef);
  const handleEmailEnter = useEnterFocuses(passwordRef);

  return (
    <AuthScreen>
      <div className="w-full max-w-sm space-y-6">
        <FormHeading description={t("description")} title={t("title")} />

        <FilingExceptionNotice />

        <SocialSignIn onError={setError} />

        <form className="flex flex-col gap-4" onSubmit={handleSignUp}>
          <div className="space-y-2">
            <Label htmlFor="name">{t("nameLabel")}</Label>
            <Input
              autoComplete="name"
              disabled={loading}
              enterKeyHint="next"
              id="name"
              onChange={handleNameChange}
              onKeyDown={handleNameEnter}
              placeholder={t("namePlaceholder")}
              required
              type="text"
              value={name}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("emailLabel")}</Label>
            <Input
              autoComplete="email"
              disabled={loading}
              enterKeyHint="next"
              id="email"
              onChange={handleEmailChange}
              onKeyDown={handleEmailEnter}
              placeholder={t("emailPlaceholder")}
              ref={emailRef}
              required
              type="email"
              value={email}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("passwordLabel")}</Label>
            <Input
              autoComplete="new-password"
              disabled={loading}
              enterKeyHint="go"
              id="password"
              onChange={handlePasswordChange}
              placeholder={t("passwordPlaceholder")}
              ref={passwordRef}
              required
              type="password"
              value={password}
            />
            <p className="text-xs text-muted-foreground">{t("passwordHint")}</p>
          </div>

          <AuthError error={error} />

          <Button className="w-full" disabled={loading} type="submit">
            {loading ? t("creatingAccount") : t("submit")}
          </Button>
        </form>

        <LegalConsent />

        <div className="flex items-center justify-center gap-1 text-sm">
          <span className="text-muted-foreground">{t("haveAccount")}</span>
          <Link
            className="font-medium text-primary hover:underline"
            to={`/login${trialLinkQuery(search)}`}
          >
            {t("signIn")}
          </Link>
        </div>
      </div>
    </AuthScreen>
  );
}
